import React from 'react';
import { call, takeEvery, put } from 'redux-saga/effects';
import { useSelector } from 'react-redux';
import {
  fetchEngineStatus,
  EngineSettingsForm,
  showToast,
  stopEngine,
  startEngine,
} from '@kineticdata/react';
import { Icon } from '@kineticdata/ui';
import { regRoute, regSaga, regHandlers, dispatch, action } from '../../store';

regRoute('SPACE_TASK_ENGINE_SETTINGS', '/spaces/edit/:spaceSlug/task/engine');

regSaga(
  takeEvery('ENTER_SPACE_TASK_ENGINE_SETTINGS', function* ({ payload }) {
    try {
      const { status, error } = yield call(fetchEngineStatus, {
        spaceSlug: payload.params.spaceSlug,
      });
      if (error) {
        yield put({
          type: 'SET_WORKFLOW_ENGINE_ERROR',
          payload: error,
        });
      } else {
        yield put({
          type: 'SET_WORKFLOW_ENGINE_STATUS',
          payload: status,
        });
      }
    } catch (e) {
      yield put({
        type: 'SET_WORKFLOW_ENGINE_ERROR',
        payload: 'There was a problem fetching engine status.',
      });
      console.warn(e);
    }
  }),
);

regSaga(
  takeEvery('STOP_ENGINE', function* ({ payload }) {
    try {
      const { message, messageType } = yield call(stopEngine, {
        spaceSlug: payload,
      });
      messageType && messageType === 'success'
        ? yield put(action('SET_WORKFLOW_ENGINE_STATUS', 'Stopped'))
        : yield put(action('SET_WORKFLOW_ENGINE_ERROR', message));
    } catch (e) {
      yield put(
        action(
          'SET_WORKFLOW_ENGINE_ERROR',
          'There was a problem stopping the Task engine.',
        ),
      );
      console.warn(e);
    }
  }),
);
regSaga(
  takeEvery('START_ENGINE', function* ({ payload }) {
    try {
      const { message, messageType } = yield call(startEngine, {
        spaceSlug: payload,
      });
      messageType && messageType === 'success'
        ? yield put(action('SET_WORKFLOW_ENGINE_STATUS', 'Running'))
        : yield put(action('SET_WORKFLOW_ENGINE_ERROR', message));
    } catch (e) {
      yield put(
        action(
          'SET_WORKFLOW_ENGINE_ERROR',
          'There was a problem starting the Task engine.',
        ),
      );
      console.warn(e);
    }
  }),
);

regHandlers({
  SET_WORKFLOW_ENGINE_STATUS: (state, { payload }) =>
    state.setIn(['page', 'workflowEngineSettings', 'status'], payload),
  SET_WORKFLOW_ENGINE_ERROR: (state, { payload }) =>
    state.setIn(['page', 'workflowEngineSettings', 'error'], payload),
});

export const handleEngineStop = spaceSlug => () => {
  dispatch('STOP_ENGINE', spaceSlug);
};

export const handleEngineStart = spaceSlug => () => {
  dispatch('START_ENGINE', spaceSlug);
};

const handleSave = () => () => {
  showToast(
    { content: `The engine settings were updated successfully.` },
    'success',
  );
};

const EngineSettingsFormLayout = ({ fields, error, buttons }) => {
  return (
    <form>
      <div className="row">
        <div className="col-12 col-xxl-8">{fields.get('Trigger Query')}</div>
        <div className="col-6 col-xxl-2">{fields.get('Max Threads')}</div>
        <div className="col-6 col-xxl-2">{fields.get('Sleep Delay')}</div>
      </div>

      {error}
      <div className="form-buttons">{buttons}</div>
    </form>
  );
};

export const SpaceEditTaskEngine = ({ spaceSlug }) => {
  const { engineRunning, engineError } = useSelector(state => ({
    engineRunning:
      state.getIn(['page', 'workflowEngineSettings', 'status'], 'Running') ===
      'Running',
    engineError: state.getIn(['page', 'workflowEngineSettings', 'error']),
  }));

  return (
    <div className="content-container">
      <div className="heading heading-sm">
        <h2 className="h3">Engine Status</h2>
      </div>

      {engineError || !engineRunning ? (
        <>
          <div className="input-group input-group--map-field">
            <div
              className="form-control-plaintext rounded bg-danger"
              style={{ maxWidth: '40vw' }}
            />
            <button
              className="btn btn-outline-primary"
              onClick={handleEngineStart(spaceSlug)}
              disabled={engineError || engineRunning}
            >
              <Icon name="play" />
              <span>Start</span>
            </button>
          </div>
          <div className="form-control-plaintext">
            <em className="fw-semibold">
              {engineError
                ? `Error: ${
                    engineError.message ? engineError.message : engineError
                  }`
                : 'Stopped'}
            </em>
          </div>
        </>
      ) : (
        <>
          <div className="input-group input-group--map-field">
            <div
              className="form-control-plaintext rounded animate-success-bg"
              style={{ maxWidth: '40vw' }}
            />
            <button
              className="btn btn-danger"
              onClick={handleEngineStop(spaceSlug)}
              disabled={engineError || !engineRunning}
            >
              <Icon name="stop-circle" />
              <span>Stop</span>
            </button>
          </div>
          <div className="form-control-plaintext">
            <em className="fw-semibold">Running</em>
          </div>
        </>
      )}

      <div className="heading heading-sm mt-4">
        <h2 className="h3">Engine Settings</h2>
      </div>
      <EngineSettingsForm
        spaceSlug={spaceSlug}
        components={{
          FormLayout: EngineSettingsFormLayout,
        }}
        onSave={handleSave}
      />
    </div>
  );
};
